import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useRoomStore } from '../store/roomStore';
import { useAuthStore } from '../store/authStore';
import { Room } from '../types';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface RoomStatePayload {
  new: {
    id: string;
    room_id: string;
    name: string;
    color: string;
    emoji: string | null;
  } | null;
  old: {
    id: string;
    room_id: string;
    name: string;
    color: string;
    emoji: string | null;
  } | null;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
}

interface RoomMemberPayload {
  new: {
    id: string;
    room_id: string;
    user_id: string;
    role: 'ADMIN' | 'USER' | 'VIEWER';
  } | null;
  old: {
    id: string;
    room_id: string;
    user_id: string;
    role: 'ADMIN' | 'USER' | 'VIEWER';
  } | null;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
}

export const useRealtimeRooms = () => {
  const { user } = useAuthStore();
  const { loadRooms, refreshRoomUserProfiles } = useRoomStore();
  const subscriptionRef = useRef<any>(null);
  const lastUpdateRef = useRef<{ [key: string]: number }>({});

  useEffect(() => {
    if (!user) {
      return;
    }

    // Subscribe to real-time changes
    const setupRealtimeSubscription = () => {
      // Clean up existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }

      // Subscribe to all relevant table changes
      subscriptionRef.current = supabase
        .channel('room-updates')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'room_state_history'
          },
          (payload: RealtimePostgresChangesPayload<any>) => {
            console.log('Room state change detected:', payload);
            // Reload rooms when state changes
            loadRooms(user.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'rooms'
          },
          (payload: RealtimePostgresChangesPayload<any>) => {
            console.log('Room update detected:', payload);
            // Reload rooms when room settings change
            loadRooms(user.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'room_members'
          },
          (payload: RealtimePostgresChangesPayload<RoomMemberPayload>) => {
            console.log('Room member change:', payload);
            // Reload rooms when members change
            loadRooms(user.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'broadcast_links'
          },
          (payload: RealtimePostgresChangesPayload<any>) => {
            console.log('Broadcast link change:', payload);
            // Reload rooms when broadcast links change
            loadRooms(user.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'room_states'
          },
          (payload: RealtimePostgresChangesPayload<RoomStatePayload>) => {
            console.log('Room states change:', payload);
            // Reload rooms when states are modified
            loadRooms(user.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'users'
          },
          async (payload: RealtimePostgresChangesPayload<any>) => {
            console.log('User profile change:', payload);
            
            // Only process UPDATE events with both new and old data
            if (payload.eventType !== 'UPDATE' || !payload.new || !payload.old) {
              return;
            }

            const newData = payload.new;
            const oldData = payload.old;

            // Check if any relevant fields have changed
            const relevantChange = 
              newData.name !== oldData.name ||
              newData.email !== oldData.email ||
              newData.avatar !== oldData.avatar;

            if (!relevantChange) {
              return;
            }

            // Get current timestamp
            const now = Date.now();
            const userId = newData.id;
            
            // Check if we've updated this user recently (within 2 seconds)
            if (lastUpdateRef.current[userId] && now - lastUpdateRef.current[userId] < 2000) {
              console.log('Skipping update - too soon after last update');
              return;
            }

            // Update the last update timestamp for this user
            lastUpdateRef.current[userId] = now;

            // Get current rooms
            const { rooms } = useRoomStore.getState();
            
            // Only refresh rooms where this user is a member
            const roomsToUpdate = rooms.filter((room: Room) => 
              room.users.some((u) => u.id === userId)
            );

            // If no rooms need updating, skip
            if (roomsToUpdate.length === 0) {
              return;
            }

            // Update each room's user profiles sequentially to prevent race conditions
            for (const room of roomsToUpdate) {
              try {
                await refreshRoomUserProfiles(room.id);
              } catch (error) {
                console.error(`Failed to refresh profiles for room ${room.id}:`, error);
                // Continue with other rooms even if one fails
              }
            }
          }
        )
        .subscribe((status: string) => {
          console.log('Realtime subscription status:', status);
        });
    };

    setupRealtimeSubscription();

    // Cleanup on unmount or user change
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [user, loadRooms, refreshRoomUserProfiles]);
};